import { Controller, useFormContext } from "react-hook-form";
import type { Control } from "react-hook-form";
import { Mail01 } from "@untitledui/icons";
import { Input, InputBase, InputGroup, NativeSelect, Select } from "@tricura/atlas-ui";
import { DateTextField } from "../../../../components/date-text-field";
import { FormGroup } from "../../../../components/form-group";
import { formatMoneyMask, moneyDigits } from "../../../../utils/money";
import type { UwInformationFormData } from "../schema";
import { CharCountHint, isAtLimit } from "../../../../components/char-count-hint";
import { SUBMISSION_TEXT_MAX_LENGTH } from "../../../../utils/field-limits";

const submissionTypes = [
    { id: "New Business", label: "New Business" },
    { id: "Renewal", label: "Renewal" },
    { id: "Endorsement", label: "Endorsement" },
    { id: "Tail", label: "Tail" },
];

/** "MM/DD/YYYY" → same day, one year later ("MM/DD/YYYY+1"). Returns null
 *  until the input is a complete date so we don't auto-fill on partials. */
function oneYearLater(mmddyyyy: string): string | null {
    const m = mmddyyyy.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (!m) return null;
    const [, mm, dd, yyyy] = m;
    return `${mm}/${dd}/${Number(yyyy) + 1}`;
}

function SubmissionTypeField({ control }: { control: Control<UwInformationFormData> }) {
    return (
        <Controller
            control={control}
            name="accountBasics.type"
            render={({ field, fieldState }) => (
                <Select
                    size="sm"
                    label="Submission Type"
                    placeholder="Select Submission Type"
                    tooltip="Type of policy transaction."
                    hint={fieldState.error?.message}
                    isInvalid={fieldState.invalid}
                    selectedKey={field.value || null}
                    onSelectionChange={(k) => field.onChange((k as string) ?? "")}
                    items={submissionTypes}
                    data-autotest="uw-information-account-basics-submission-type-select"
                >
                    {(item) => (
                        <Select.Item
                            id={item.id}
                            data-autotest={`uw-information-account-basics-submission-type-option-${String(item.id).toLowerCase().replaceAll(" ", "-")}`}
                        >
                            {item.label}
                        </Select.Item>
                    )}
                </Select>
            )}
        />
    );
}

function MailingAddressField({ control }: { control: Control<UwInformationFormData> }) {
    return (
        <Controller
            control={control}
            name="accountBasics.mailingAddress"
            render={({ field, fieldState }) => (
                <Input
                    size="sm"
                    label="Mailing Address"
                    placeholder="Enter Mailing Address"
                    data-autotest="uw-information-account-basics-mailing-address-input"
                    // No tooltip on purpose. The mock draws the ⓘ but no copy
                    // exists for it anywhere — "tooltip" appears in none of the
                    // epic's tickets — and Gustavo would rather ship the field
                    // without one than with text we made up. Add it when design
                    // supplies the wording.
                    hint={fieldState.error?.message}
                    isInvalid={fieldState.invalid}
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                />
            )}
        />
    );
}

export function AccountBasicsSection() {
    const { control, setValue } = useFormContext<UwInformationFormData>();

    return (
        <FormGroup title="Account Basics">
            <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
                <Controller
                    control={control}
                    name="accountBasics.title"
                    render={({ field, fieldState }) => (
                        <div>
                            <Input
                                size="sm"
                                label="Submission Title"
                                isRequired
                                placeholder="Enter submission title"
                                tooltip="Internal reference for this submission."
                                                            isInvalid={fieldState.invalid || isAtLimit(field.value, SUBMISSION_TEXT_MAX_LENGTH)}
                                value={field.value}
                                onChange={field.onChange}
                                onBlur={field.onBlur}
                                maxLength={SUBMISSION_TEXT_MAX_LENGTH}
                                data-autotest="uw-information-account-basics-submission-title-input"
                            />
                            <CharCountHint value={field.value} maxLength={SUBMISSION_TEXT_MAX_LENGTH} error={fieldState.error?.message} />
                        </div>
                    )}
                />
                <Controller
                    control={control}
                    name="accountBasics.fni"
                    render={({ field, fieldState }) => (
                        <div>
                            <Input
                                size="sm"
                                label="FNI (Fiscal Name of Insured)"
                                placeholder="Enter Fiscal Name of Insured"
                                tooltip="Legal entity name on the policy."
                                                            isInvalid={fieldState.invalid || isAtLimit(field.value, SUBMISSION_TEXT_MAX_LENGTH)}
                                value={field.value}
                                onChange={field.onChange}
                                onBlur={field.onBlur}
                                maxLength={SUBMISSION_TEXT_MAX_LENGTH}
                                data-autotest="uw-information-account-basics-insured-name-input"
                            />
                            <CharCountHint value={field.value} maxLength={SUBMISSION_TEXT_MAX_LENGTH} error={fieldState.error?.message} />
                        </div>
                    )}
                />
                {/* OR-1455 — the dec page prints the insured's mailing address
                 * separately from the primary location's. It hangs off the
                 * submission (not the named insured), so it belongs here next
                 * to the FNI rather than as a column on the Named Insureds
                 * table — the other option in Andressa's mock, which the data
                 * model can't back. */}
                <MailingAddressField control={control} />
            </div>

            {/* OR-1455 — the dec replaces a "Senior Living Communities" anchor
             * with this, and `render_policy_docx` requires it. Unlike the other
             * dec fields it appears in no mock and no ticket, so the placement,
             * the label and the wire key are all ours: it sits with the
             * insured's identity, full width because it's prose. Confirm with
             * design and backend. */}
            <div className="grid grid-cols-1 gap-5">
                <Controller
                    control={control}
                    name="accountBasics.businessDescription"
                    render={({ field, fieldState }) => (
                        <Input
                            size="sm"
                            label="Business Description"
                            placeholder="Enter Business Description"
                            data-autotest="uw-information-account-basics-business-description-input"
                            hint={fieldState.error?.message}
                            isInvalid={fieldState.invalid}
                            value={field.value}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                        />
                    )}
                />
            </div>

            {/* OR-1455 — Mailing Address takes the first row's third slot, so
             * Submission Type moves down to join the dates on a four-up row —
             * Andressa's mock. */}
            <div className="grid grid-cols-1 gap-5 md:grid-cols-4">
                <SubmissionTypeField control={control} />
                <Controller
                    control={control}
                    name="accountBasics.renewalDate"
                    render={({ field, fieldState }) => (
                        <DateTextField
                            label="Renewal Date"
                            isRequired
                            isInvalid={fieldState.invalid}
                            hint={fieldState.error?.message}
                            defaultValue={field.value}
                            onChange={(v) => {
                                field.onChange(v);
                                // Auto-fill Expiration with the same date one year
                                // later once the user completes a full Renewal date.
                                const exp = oneYearLater(v);
                                if (exp) setValue("accountBasics.expirationDate", exp, { shouldValidate: true, shouldDirty: true });
                            }}
                            onBlur={field.onBlur}
                            data-autotest="uw-information-account-basics-renewal-date-input"
                        />
                    )}
                />
                <Controller
                    control={control}
                    name="accountBasics.expirationDate"
                    render={({ field, fieldState }) => (
                        <DateTextField
                            label="Expiration Date"
                            isRequired
                            isInvalid={fieldState.invalid}
                            hint={fieldState.error?.message}
                            defaultValue={field.value}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            data-autotest="uw-information-account-basics-expiration-date-input"
                        />
                    )}
                />
                <Controller
                    control={control}
                    name="accountBasics.submissionDate"
                    render={({ field, fieldState }) => (
                        <DateTextField
                            label="Date of Submission"
                            isInvalid={fieldState.invalid}
                            hint={fieldState.error?.message}
                            defaultValue={field.value}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            data-autotest="uw-information-account-basics-submission-date-input"
                        />
                    )}
                />
            </div>

            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                <Controller
                    control={control}
                    name="accountBasics.expiringPremium"
                    render={({ field }) => (
                        <InputGroup
                            size="sm"
                            label="Total Expiring Premium"
                            prefix="$"
                            trailingAddon={
                                <NativeSelect
                                    aria-label="Currency"
                                    hideChevron
                                    size="sm"
                                    tabIndex={-1}
                                    selectClassName="pointer-events-none"
                                    options={[{ value: "USD", label: "USD" }]}
                                />
                            }
                        >
                            <InputBase
                                type="tel"
                                inputMode="decimal"
                                placeholder="0.00"
                                value={formatMoneyMask(field.value ?? "")}
                                onChange={(e) => field.onChange(moneyDigits(e.currentTarget.value))}
                                onBlur={field.onBlur}
                                data-autotest="uw-information-account-basics-expiring-premium-input"
                            />
                        </InputGroup>
                    )}
                />
                <Controller
                    control={control}
                    name="accountBasics.expiringDeductible"
                    render={({ field }) => (
                        <InputGroup
                            size="sm"
                            label="Expiring Deductible"
                            prefix="$"
                            trailingAddon={
                                <NativeSelect
                                    aria-label="Currency"
                                    hideChevron
                                    size="sm"
                                    tabIndex={-1}
                                    selectClassName="pointer-events-none"
                                    options={[{ value: "USD", label: "USD" }]}
                                />
                            }
                        >
                            <InputBase
                                type="tel"
                                inputMode="decimal"
                                placeholder="0.00"
                                value={formatMoneyMask(field.value ?? "")}
                                onChange={(e) => field.onChange(moneyDigits(e.currentTarget.value))}
                                onBlur={field.onBlur}
                                data-autotest="uw-information-account-basics-expiring-deductible-input"
                            />
                        </InputGroup>
                    )}
                />
            </div>

            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                <Controller
                    control={control}
                    name="accountBasics.accountManagerName"
                    render={({ field, fieldState }) => (
                        <div>
                            <Input
                                size="sm"
                                label="Account Manager Name"
                                placeholder="Enter Account Manager Name"
                                tooltip="Primary contact on the broker side."
                                                            isInvalid={fieldState.invalid || isAtLimit(field.value, SUBMISSION_TEXT_MAX_LENGTH)}
                                value={field.value}
                                onChange={field.onChange}
                                onBlur={field.onBlur}
                                maxLength={SUBMISSION_TEXT_MAX_LENGTH}
                                data-autotest="uw-information-account-basics-account-manager-name-input"
                            />
                            <CharCountHint value={field.value} maxLength={SUBMISSION_TEXT_MAX_LENGTH} error={fieldState.error?.message} />
                        </div>
                    )}
                />
                <Controller
                    control={control}
                    name="accountBasics.accountManagerEmail"
                    render={({ field, fieldState }) => (
                        <div>
                            <Input
                                size="sm"
                                label="Account Manager Email"
                                placeholder="Enter Account Manager Email"
                                type="email"
                                icon={Mail01}
                                                            isInvalid={fieldState.invalid || isAtLimit(field.value, SUBMISSION_TEXT_MAX_LENGTH)}
                                value={field.value}
                                onChange={field.onChange}
                                onBlur={field.onBlur}
                                maxLength={SUBMISSION_TEXT_MAX_LENGTH}
                                data-autotest="uw-information-account-basics-account-manager-email-input"
                            />
                            <CharCountHint value={field.value} maxLength={SUBMISSION_TEXT_MAX_LENGTH} error={fieldState.error?.message} />
                        </div>
                    )}
                />
            </div>
        </FormGroup>
    );
}
